import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BriefingData, generateMarketReport } from '../../shared/services/aiOrchestrator';
import { Sparkles, Building, MapPin, Globe, CheckCircle2, ArrowRight, X, Phone } from 'lucide-react';

interface OnboardingProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultSegment?: string;
  defaultSlug?: string;
}

export const OnboardingProposalModal: React.FC<OnboardingProposalModalProps> = ({
  isOpen,
  onClose,
  defaultSegment = 'Gastronomia & Delivery',
  defaultSlug = 'empresa'
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<BriefingData>({
    companyName: '',
    segment: defaultSegment,
    cityState: 'Chapecó / SC',
    instagramHandle: '',
    hasWebsite: false,
    mainGoal: 'Aumentar vendas e ter site/app próprio sem comissões',
    whatsapp: '49991172320'
  });
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      setIsGenerating(true);
      setTimeout(() => {
        const report = generateMarketReport(formData);
        localStorage.setItem('youmachine_current_briefing', JSON.stringify(formData));
        localStorage.setItem('youmachine_current_report', JSON.stringify(report));
        setIsGenerating(false);
        onClose();
        navigate(`/${defaultSlug}/proposta-canvas`);
      }, 1200);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-slate-300 rounded-2xl max-w-xl w-full p-8 relative overflow-hidden text-[#0F172A]">
         
         <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-black font-bold">
            <X className="w-5 h-5" />
         </button>

         <div className="flex items-center gap-2 text-xs font-bold text-[#2563EB] uppercase tracking-widest mb-1">
            <Sparkles className="w-4 h-4 text-amber-500 fill-amber-500" /> Autoconstrutor de Propostas IA
         </div>
         <h2 className="text-2xl font-heading font-black text-[#0F172A]">
            Diagnóstico Comercial & Proposta Personalizada
         </h2>
         <p className="text-xs text-slate-500 font-light mt-1 mb-6">
            Preencha os dados em 30 segundos. Nossa IA fará o estudo de mercado e gerará a proposta em formato Canvas Vertical.
         </p>

         {/* Step Progress Bar Google Style */}
         <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
               <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#2563EB]' : 'bg-slate-200'}`}
               />
            ))}
         </div>

         {isGenerating ? (
            <div className="py-12 text-center space-y-4">
               <div className="w-12 h-12 rounded-full border-4 border-blue-600 border-t-transparent animate-spin mx-auto" />
               <h3 className="font-bold text-base text-[#0F172A]">Analisando Dados de Mercado & Posicionamento...</h3>
               <p className="text-xs text-slate-500">Consultando robôs de busca e gerando gráfico de retorno sobre o investimento.</p>
            </div>
         ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
               
               {step === 1 && (
                  <div className="space-y-4">
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Nome da sua Empresa ou Marca</label>
                        <input
                           type="text"
                           required
                           placeholder="Ex: Bruta Burger / Vanguard Imóveis"
                           value={formData.companyName}
                           onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                        />
                     </div>
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Segmento de Atuação</label>
                        <select
                           value={formData.segment}
                           onChange={e => setFormData({ ...formData, segment: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none text-slate-700"
                        >
                           <option>Gastronomia & Delivery</option>
                           <option>Imobiliária & Construtora</option>
                           <option>Advocacia & Consultoria</option>
                           <option>Clínica de Saúde & Estética</option>
                           <option>Concessionária & Veículos</option>
                           <option>Arquitetura & Engenharia</option>
                           <option>E-commerce & Moda</option>
                        </select>
                     </div>
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Cidade e Estado de Atuação</label>
                        <input
                           type="text"
                           required
                           placeholder="Ex: Chapecó / SC"
                           value={formData.cityState}
                           onChange={e => setFormData({ ...formData, cityState: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                        />
                     </div>
                  </div>
               )}

               {step === 2 && (
                  <div className="space-y-4">
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Perfil do Instagram (ou @handle)</label>
                        <input
                           type="text"
                           placeholder="@suaempresa"
                           value={formData.instagramHandle}
                           onChange={e => setFormData({ ...formData, instagramHandle: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                        />
                     </div>
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Sua empresa já possui site ativo atualmente?</label>
                        <div className="grid grid-cols-2 gap-3">
                           <button
                              type="button"
                              onClick={() => setFormData({ ...formData, hasWebsite: true })}
                              className={`py-3 rounded-xl text-xs font-bold border ${formData.hasWebsite ? 'bg-[#2563EB] text-white border-blue-700' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
                           >
                              Sim, temos site
                           </button>
                           <button
                              type="button"
                              onClick={() => setFormData({ ...formData, hasWebsite: false })}
                              className={`py-3 rounded-xl text-xs font-bold border {!formData.hasWebsite ? 'bg-[#2563EB] text-white border-blue-700' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
                           >
                              Não, 100% offline
                           </button>
                        </div>
                     </div>
                  </div>
               )}

               {step === 3 && (
                  <div className="space-y-4">
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Seu Maior Objetivo Comercial</label>
                        <select
                           value={formData.mainGoal}
                           onChange={e => setFormData({ ...formData, mainGoal: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none text-slate-700"
                        >
                           <option>Aumentar vendas e ter site/app próprio sem comissões</option>
                           <option>Estar em 1º lugar no Google em Chapecó e região</option>
                           <option>Fortalecer a marca e passar autoridade premium</option>
                           <option>Escalar vendas de serviços e captar novos clientes diários</option>
                        </select>
                     </div>
                     <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">WhatsApp para Envio do PDF</label>
                        <input
                           type="tel"
                           required
                           placeholder="DDD + Número"
                           value={formData.whatsapp}
                           onChange={e => setFormData({ ...formData, whatsapp: e.target.value })}
                           className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none"
                        />
                     </div>
                  </div>
               )}

               <div className="pt-4 flex justify-between gap-3 border-t border-slate-100">
                  {step > 1 ? (
                     <button
                        type="button"
                        onClick={() => setStep(step - 1)}
                        className="px-5 py-3 rounded-xl text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200"
                     >
                        Voltar
                     </button>
                  ) : <div />}

                  <button
                     type="submit"
                     className="px-6 py-3 rounded-xl text-xs font-bold text-white bg-[#2563EB] hover:bg-blue-700 border border-blue-600 flex items-center gap-2"
                  >
                     {step === 3 ? '⚡ Gerar Proposta Canvas IA' : 'Próximo Passo →'}
                  </button>
               </div>

            </form>
         )}

      </div>
    </div>
  );
};
